import { VMHAConditionKey, type VMHAStatusDetails } from 'api-client/model'
import { CLUSTER_HOST_SCHEDULING } from 'app/constants'
import { ephemeralSharedStorageDoc } from 'app/docLinks'
import ExternalLink from 'core/components/ExternalLink'
import type { BadgeVariant } from 'core/elements/badge/Badge'
import { DEFAULT_NODE_GROUP_STATUS, type NodeGroupStatus } from 'k8s/components/dashboard/helpers'
import { errorStatuses, pendingStatuses } from '../selectors'
import { Status, type VMHACondition } from './model'

interface HostWithRoleStatus {
  role_status?: string | null
}

export const generateHostStatus = (
  hosts: HostWithRoleStatus[] | null | undefined,
): Array<NodeGroupStatus> => {
  const hostStatus = JSON.parse(
    JSON.stringify({
      success: DEFAULT_NODE_GROUP_STATUS.success,
      error: DEFAULT_NODE_GROUP_STATUS.error,
      warning: DEFAULT_NODE_GROUP_STATUS.warning,
    }),
  )

  hosts?.forEach((host) => {
    if (['active', 'ok'].includes(host?.role_status)) {
      hostStatus.success.label++
    } else if (['failed', 'error'].includes(host?.role_status)) {
      hostStatus.error.label++
    } else {
      hostStatus.warning.label++
    }
  })

  return Object.values(hostStatus)
}

export const getClusterHostGroupBadgeVariant = (status: string): BadgeVariant => {
  switch (status) {
    case Status.Healthy:
      return 'success'
    case Status.Unhealthy:
      return 'error'
    case Status.Maintenance:
      return 'warning'
    case Status.Error:
      return 'error'
    case Status.Unknown:
    default:
      return 'unknown'
  }
}

interface HostUsage {
  usage?: {
    computeAllocation?: { current?: number; max?: number }
    memory?: { current?: number; max?: number }
    disk?: { current?: number; max?: number }
  }
}

export const getAggregatedUsage = (hostList: HostUsage[] | null | undefined) => {
  const totalUsage = {
    cpuUsage: { current: 0, total: 0 },
    memoryUsage: { current: 0, total: 0 },
    storageUsage: { current: 0, total: 0 },
  }

  // biome-ignore lint/complexity/noForEach: I am happy with forEach here
  hostList?.forEach((host) => {
    totalUsage.cpuUsage.current += host?.usage?.computeAllocation?.current || 0
    totalUsage.cpuUsage.total += host?.usage?.computeAllocation?.max || 0
    totalUsage.memoryUsage.current += host?.usage?.memory?.current || 0
    totalUsage.memoryUsage.total += host?.usage?.memory?.max || 0
    totalUsage.storageUsage.current += host?.usage?.disk?.current || 0
    totalUsage.storageUsage.total += host?.usage?.disk?.max || 0
  })

  // Round off the values to 2 decimal places
  // biome-ignore lint/complexity/noForEach: I am happy with forEach here
  Object.keys(totalUsage).forEach((usageType) => {
    totalUsage[usageType].current = parseFloat(totalUsage[usageType].current.toFixed(2))
    totalUsage[usageType].total = parseFloat(totalUsage[usageType].total.toFixed(2))
  })

  return totalUsage
}

export const getVMHAConditions = (includeHASlaveStatus: boolean = true) =>
  [
    {
      key: VMHAConditionKey.TwoActiveHosts,
      label: 'At least two active hosts exist in the cluster.',
    },
    {
      key: VMHAConditionKey.SharedStorage,
      label: (
        <>
          All VMs across cluster hosts either use a block storage volume as the root disk or{' '}
          <ExternalLink url={ephemeralSharedStorageDoc}>Ephemeral Shared Storage</ExternalLink> for
          VMs using ephemeral storage for the root disk.
        </>
      ),
    },
    {
      key: VMHAConditionKey.ImageLibrary,
      label: 'At least two hosts have the image library role assigned.',
    },
    {
      key: VMHAConditionKey.PersistentStorage,
      label: 'At least two hosts have the persistent storage role assigned.',
    },
    ...(includeHASlaveStatus
      ? [
          {
            key: VMHAConditionKey.HaSlaveStatus,
            label: 'The VM HA role is successfully applied to all hypervisor hosts in the cluster.',
          },
        ]
      : []),
  ] as const

export const mapVMHAConditions = (
  vmhaClusters: VMHAStatusDetails,
  includeHASlaveStatus: boolean = true,
): VMHACondition[] => {
  const conditions = vmhaClusters?.conditions ?? {}

  return getVMHAConditions(includeHASlaveStatus)?.map(({ key, label }) => ({
    key,
    label,
    passed: conditions[key] === 'pass',
    degraded: conditions[key] === 'degraded',
  }))
}

const VMWARE_ROLES = ['pf9-ostackhost-neutron-vmw', 'pf9-glance-role-vmw']

interface EligibilityHost {
  hostconfig_id?: string | null
  gpuModels?: string[]
  responding?: string
  schedulingStatus?: string
  v1Roles?: string[]
  vmwareRolesIncomplete?: boolean
  roleStatuses?: Array<{ status: string }>
}

interface EligibilityCluster {
  gpu?: { enabled?: boolean }
}

export const getHostEligibilityError = (
  host: EligibilityHost,
  cluster: EligibilityCluster,
): string | null => {
  const isGpuHost = (host.gpuModels?.length ?? 0) > 0
  const isGpuCluster = cluster?.gpu?.enabled === true
  if (!isGpuHost && isGpuCluster) {
    return "Host GPU capability does not match the cluster's GPU configuration."
  }

  if (host.responding === 'offline') {
    return 'Host is offline. Resolve connectivity before adding to a cluster.'
  }

  if (host.schedulingStatus === CLUSTER_HOST_SCHEDULING.disabled) {
    return 'Host is in maintenance mode. Disable maintenance mode before adding to a cluster.'
  }

  const hasVmwareRole = host.v1Roles?.some((r: string) => VMWARE_ROLES.includes(r))
  if (hasVmwareRole || host.vmwareRolesIncomplete) {
    return 'VMware hosts cannot be added to a cluster using this flow.'
  }

  const hasRoleConverging = host.roleStatuses?.some((rs) => pendingStatuses.includes(rs.status))
  if (hasRoleConverging) {
    return 'A role operation is already in progress on this host. Wait for it to complete before adding to a cluster.'
  }

  const hasRoleError = host.roleStatuses?.some((rs) => errorStatuses.includes(rs.status))
  if (hasRoleError) {
    return 'This host has a role error. Resolve the error before adding to a cluster.'
  }

  return null
}
